/**
 * banner-slider.js
 *
 * Custom element: <banner-slider>
 *
 * Architecture decisions (CWV rationale):
 * ─────────────────────────────────────────
 * LCP  → Script is `defer`-loaded; first slide already visible before JS parses.
 *         No JS is needed to render slide 1 — pure HTML/CSS handles it.
 * INP  → Dot/arrow click handlers are lightweight: they just set scrollLeft
 *         (CSS scroll-snap does the animation on the GPU compositor thread).
 *         No forced reflows; all mutations are wrapped in requestAnimationFrame.
 * CLS  → The track and each slide have CSS `aspect-ratio` set server-side, so
 *         layout is fully stable before this script executes. JS never touches
 *         height, width, top, or padding properties.
 *
 * No external dependencies. ~3 KB unminified.
 */

class BannerSlider extends HTMLElement {
  constructor() {
    super();

    // ── State ──────────────────────────────────────────────────────────────
    this._currentIndex  = 0;
    this._totalSlides   = 0;
    this._autoplayTimer = null;
    this._isPlaying     = false;
    this._isReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    // Bind event handlers once (avoids creating new functions on each call)
    this._onDotClick    = this._onDotClick.bind(this);
    this._onArrowClick  = this._onArrowClick.bind(this);
    this._onScrollEnd   = this._onScrollEnd.bind(this);
    this._onKeyDown     = this._onKeyDown.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerUp   = this._onPointerUp.bind(this);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    /**
     * INP best-practice: heavy init only fires when the section is near
     * the viewport. rootMargin:'200px' pre-initialises slightly before
     * the section scrolls into view so auto-play is ready immediately.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._init();
            observer.unobserve(this);
          }
        });
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(this);
  }

  disconnectedCallback() {
    this._stopAutoplay();
    this._removeEventListeners();
  }

  // ── Initialisation ────────────────────────────────────────────────────────

  _init() {
    this._track       = this.querySelector('.banner-slider__track');
    this._slides      = this.querySelectorAll('.banner-slide');
    this._dots        = this.querySelectorAll('.banner-slider__dot');
    this._prevBtn     = this.querySelector('.banner-slider__arrow--prev');
    this._nextBtn     = this.querySelector('.banner-slider__arrow--next');
    this._totalSlides = this._slides.length;

    if (this._totalSlides <= 1) return; // nothing to slide

    this._addEventListeners();

    // Start auto-play only when section is actually visible
    const playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._maybeStartAutoplay();
          } else {
            this._stopAutoplay();
          }
        });
      },
      { threshold: 0.3 }
    );
    playObserver.observe(this);
  }

  // ── Event listeners ───────────────────────────────────────────────────────

  _addEventListeners() {
    // Dot navigation
    this._dots.forEach((dot) =>
      dot.addEventListener('click', this._onDotClick)
    );

    // Arrow navigation
    if (this._prevBtn) this._prevBtn.addEventListener('click', this._onArrowClick);
    if (this._nextBtn) this._nextBtn.addEventListener('click', this._onArrowClick);

    // Sync dot state when user manually swipes (scroll-snap driven)
    // 'scrollend' has good modern browser support; fall back to debounced 'scroll'
    if ('onscrollend' in this._track) {
      this._track.addEventListener('scrollend', this._onScrollEnd);
    } else {
      this._track.addEventListener('scroll', this._debounce(this._onScrollEnd, 150));
    }

    // Keyboard accessibility (←/→ when slider is focused)
    this.addEventListener('keydown', this._onKeyDown);
    this.setAttribute('tabindex', '0');

    // Pause autoplay on hover / touch (UX convention)
    this.addEventListener('pointerenter', this._onPointerDown);
    this.addEventListener('pointerdown',  this._onPointerDown);
    this.addEventListener('pointerleave', this._onPointerUp);
    this.addEventListener('pointerup',    this._onPointerUp);
  }

  _removeEventListeners() {
    this._dots?.forEach((dot) =>
      dot.removeEventListener('click', this._onDotClick)
    );
    this._prevBtn?.removeEventListener('click', this._onArrowClick);
    this._nextBtn?.removeEventListener('click', this._onArrowClick);
    this._track?.removeEventListener('scrollend', this._onScrollEnd);
    this.removeEventListener('keydown',      this._onKeyDown);
    this.removeEventListener('pointerenter', this._onPointerDown);
    this.removeEventListener('pointerdown',  this._onPointerDown);
    this.removeEventListener('pointerleave', this._onPointerUp);
    this.removeEventListener('pointerup',    this._onPointerUp);
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  _onDotClick(e) {
    const index = parseInt(e.currentTarget.dataset.index, 10);
    this.goTo(index);
    this._restartAutoplay();
  }

  _onArrowClick(e) {
    const direction = e.currentTarget.dataset.direction;
    if (direction === 'prev') {
      this.prev();
    } else {
      this.next();
    }
    this._restartAutoplay();
  }

  /**
   * Sync dot UI after a CSS scroll-snap gesture.
   * Reads scrollLeft to determine which slide snapped into view.
   * Uses rAF to avoid forced reflow on the main thread (INP).
   */
  _onScrollEnd() {
    requestAnimationFrame(() => {
      const slideWidth = this._track.offsetWidth;
      if (slideWidth === 0) return;
      const index = Math.round(this._track.scrollLeft / slideWidth);
      if (index !== this._currentIndex) {
        this._currentIndex = index;
        this._updateDots();
        this._updateAriaLive();
      }
    });
  }

  _onKeyDown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.prev();
        this._restartAutoplay();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.next();
        this._restartAutoplay();
        break;
    }
  }

  _onPointerDown() { this._stopAutoplay(); }
  _onPointerUp()   { this._maybeStartAutoplay(); }

  // ── Navigation API ────────────────────────────────────────────────────────

  goTo(index) {
    if (index < 0 || index >= this._totalSlides) return;
    this._currentIndex = index;

    /**
     * CWV: We change scrollLeft (scroll position), NOT width/height/top/left.
     * CSS scroll-snap animates on the compositor thread → zero main-thread work.
     * Wrap in rAF to batch with any pending paint.
     */
    requestAnimationFrame(() => {
      this._track.scrollTo({
        left: this._track.offsetWidth * index,
        behavior: this._isReducedMotion ? 'instant' : 'smooth',
      });
      this._updateDots();
      this._updateAriaLive();
    });
  }

  next() {
    this.goTo((this._currentIndex + 1) % this._totalSlides);
  }

  prev() {
    this.goTo(
      (this._currentIndex - 1 + this._totalSlides) % this._totalSlides
    );
  }

  // ── Dot / ARIA state ─────────────────────────────────────────────────────

  /** GPU-safe: only toggles aria-selected attribute; CSS handles visual change */
  _updateDots() {
    this._dots.forEach((dot, i) => {
      dot.setAttribute(
        'aria-selected',
        i === this._currentIndex ? 'true' : 'false'
      );
    });
  }

  _updateAriaLive() {
    // Announce current slide to screen readers
    this._track.setAttribute(
      'aria-live',
      this._isPlaying ? 'off' : 'polite'
    );
  }

  // ── Auto-play ─────────────────────────────────────────────────────────────

  _maybeStartAutoplay() {
    const enabled = this.dataset.autoplay === 'true';
    if (!enabled || this._isReducedMotion) return;
    this._startAutoplay();
  }

  _startAutoplay() {
    if (this._autoplayTimer) return; // already running
    const speed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    this._isPlaying   = true;
    this._autoplayTimer = setInterval(() => this.next(), speed);
  }

  _stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }
    this._isPlaying = false;
  }

  _restartAutoplay() {
    this._stopAutoplay();
    this._maybeStartAutoplay();
  }

  // ── Utilities ────────────────────────────────────────────────────────────

  /** Debounce helper — keeps resize/scroll handlers off the hot path (INP) */
  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

// Register custom element only once
if (!customElements.get('banner-slider')) {
  customElements.define('banner-slider', BannerSlider);
}